import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  ArrowLeft,
  CheckCircle2,
  Loader2,
  RefreshCw,
  ShieldCheck,
  Star,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { updatePatientDetails, getPatientByPatientId } from "@/dispatchers/patientDispatchers";
import useTitanApp from "@/hooks/useTitanApp";
import {
  PDS_FIELDS,
  toPdsDemographics,
  type LocalRecordInput,
  type PdsAddressEntry,
  type PdsContactPoint,
  type PdsDemographics,
  type PdsFieldKind,
  type PdsRecord,
} from "./pdsRecord";
import { cn } from "@/lib/utils";

import { Skeleton } from "@mui/material";
import PdsSkeleton from "./PdsSkeleton";

export interface PdsSyncDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Local TITANVerse demographics for the patient currently in view. */
  localRecord?: any;
  /** NHS PDS (Spine) record or raw API response. */
  pdsRecord?: any;
  /** Called with the values the pharmacist chose to apply to the local record. */
  onApply?: (applied: PdsDemographics) => void;
  /** Optional custom sync callback. */
  onSyncFromPds?: (applied: PdsDemographics, localRecord: any) => Promise<void>;
  isLoading?: boolean;
  onDarkMode?: boolean;
}

type Choice = "local" | "pds";

const NHS_BLUE = "#005EB8";

// Stable, order-independent string used to compare a field across records.
function normField(rec: PdsDemographics, key: keyof PdsDemographics): string {
  const v = rec[key];
  if (Array.isArray(v)) {
    return v
      .map((e) =>
        "value" in e ? `${e.label}:${e.value}` : `${e.label}:${e.text}`,
      )
      .map((s) => s.trim().toLowerCase())
      .sort()
      .join("|");
  }
  return String(v ?? "")
    .trim()
    .toLowerCase();
}

const phoneKey = (p: PdsContactPoint) =>
  p.value.replace(/\s+/g, "").toLowerCase();
const addressKey = (a: PdsAddressEntry) =>
  a.text.replace(/\s+/g, " ").trim().toLowerCase();
const entryKey = (e: PdsContactPoint | PdsAddressEntry) =>
  "value" in e ? phoneKey(e) : addressKey(e);

function dedupePhones(list: PdsContactPoint[]): PdsContactPoint[] {
  const seen = new Set<string>();
  return list.filter((p) => {
    const k = p.value.replace(/\s+/g, "").toLowerCase();
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

function dedupeAddresses(list: PdsAddressEntry[]): PdsAddressEntry[] {
  const seen = new Set<string>();
  return list.filter((a) => {
    const k = a.text.replace(/\s+/g, " ").trim().toLowerCase();
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

// The PDS lookup API can respond with `null`, or with an object where every
// field is null/0/empty (no match found upstream). Either case must never be
// synced into the local record — doing so wipes out real patient data with
// blanks. This checks the *raw* API response (not the demo-fallback record).
function isEmptyPdsResponse(raw: any): boolean {
  if (!raw) return true;
  const rec = toPdsDemographics(raw);
  return (
    !rec.name &&
    !rec.nhsNumber &&
    !rec.dateOfBirth &&
    !rec.gender &&
    !rec.email &&
    !rec.gpPractice &&
    !rec.nominatedPharmacy &&
    !rec.preferredContactMethod &&
    !rec.language &&
    !rec.birthPlace &&
    !rec.deceased &&
    rec.phones.length === 0 &&
    rec.addresses.length === 0
  );
}

function buildUpdatePayload(
  localRecord: any,
  pdsRecordRaw: any,
  result: PdsDemographics,
  currentUserEmail?: string,
) {
  const { patientName, ...detailsWithoutName } = localRecord || {};

  const nameVal = (result?.name ?? "").trim();
  let title = "";
  let foreName = "";
  let middleName = "";
  let surName = "";

  if (nameVal && nameVal !== "—") {
    const parts = nameVal.split(/\s+/);
    const titles = ["mr", "mrs", "ms", "miss", "dr", "prof", "rev"];
    let idx = 0;
    if (parts.length > 0 && titles.includes(parts[0].toLowerCase())) {
      title = parts[0];
      idx = 1;
    }
    const nameParts = parts.slice(idx);
    if (nameParts.length === 1) {
      foreName = nameParts[0];
      surName = nameParts[0];
    } else if (nameParts.length === 2) {
      foreName = nameParts[0];
      surName = nameParts[1];
    } else if (nameParts.length > 2) {
      foreName = nameParts[0];
      middleName = nameParts.slice(1, -1).join(" ");
      surName = nameParts[nameParts.length - 1];
    }
  }

  // 2. Gender / Sex mapping (backend: 0=NotKnown, 1=Male, 2=Female)
  let sex = 0;
  const genderVal = (result?.gender ?? "").trim().toLowerCase();
  if (genderVal && genderVal !== "—") {
    if (genderVal.includes("female") || genderVal === "f") {
      sex = 2;
    } else if (genderVal.includes("male") || genderVal === "m") {
      sex = 1;
    }
  }

  // 3. Date of Birth (directly overwriting localRecord). PDS returns a bare
  // "YYYY-MM-DD" string, but update-patient-details expects a full ISO
  // datetime, so normalise it the same way the other patient-update flows do.
  const dobRaw = (result?.dateOfBirth || (result as any)?.dob || "").trim();
  const dobVal = dobRaw && dobRaw !== "—" ? dobRaw : "";
  const dob = dobVal ? new Date(dobVal).toISOString() : "";

  // 4. NHS Number
  const nhsVal = (result?.nhsNumber ?? "").trim();
  const nhsNumber = nhsVal && nhsVal !== "—" ? nhsVal : "";

  // 5. Email
  const emailVal = (result?.email ?? "").trim();
  const email = emailVal && emailVal !== "—" ? emailVal : "";

  // 6. Addresses (PdsAddressEntry uses .text property). The structured
  let address = "";
  let preferredAddress: any = null;
  if (Array.isArray(result?.addresses) && result.addresses.length > 0) {
    const prefAddrObj =
      result.addresses.find((a) => a.preferred) || result.addresses[0];
    const textVal = (prefAddrObj?.text || (prefAddrObj as any)?.value || "").trim();
    if (textVal && textVal !== "—") {
      address = textVal;

      const normalize = (s: string) => s.replace(/\s+/g, " ").trim().toLowerCase();
      const localAddr = localRecord?.preferredAddress?.address;
      const pdsAddr = pdsRecordRaw?.preferredAddress?.address;
      const pdsAddrText = toPdsDemographics(pdsRecordRaw).addresses[0]?.text ?? "";

      const sourceAddr =
        pdsAddr && pdsAddrText && normalize(pdsAddrText) === normalize(textVal)
          ? pdsAddr
          : localAddr;

      if (sourceAddr) {
        preferredAddress = {
          ...localRecord?.preferredAddress,
          address: {
            ...sourceAddr,
            addressId: localAddr?.addressId || sourceAddr?.addressId || undefined,
            addressLine1: address,
          },
        };
      }
    }
  }
  if (!address) {
    preferredAddress = {
      address: {
        addressId: localRecord?.preferredAddress?.address?.addressId || undefined,
        houseName: "",
        streetName: "",
        locality: "",
        town: "",
        county: "",
        postcode: "",
        addressLine1: "",
        addressLine2: "",
      },
    };
  }

  // 7. Contacts / Phone numbers (PdsContactPoint uses .value property)
  let contacts: any[] = [];
  if (Array.isArray(result?.phones) && result.phones.length > 0) {
    const prefPhoneObj =
      result.phones.find((p) => p.preferred) || result.phones[0];
    const phoneVal = (prefPhoneObj?.value || "").trim();
    if (phoneVal && phoneVal !== "—") {
      contacts = [
        {
          patientContactId:
            localRecord?.contacts?.[0]?.patientContactId || undefined,
          contactType: 4, // Primary / Mobile contact
          value: phoneVal,
        },
      ];
    }
  }

  // 8. Construct payload with updatedOn and updatedBy
  const payload = {
    ...detailsWithoutName,
    patientID:
      localRecord?.patientID || localRecord?.patientId || localRecord?._id,
    title,
    foreName,
    middleName,
    surName,
    dob,
    sex,
    nhsNumber,
    address,
    email,
    contacts,
    preferredAddress,
    updatedOn: new Date().toISOString(),
    updatedBy: currentUserEmail || localRecord?.updatedBy || "",
  };

  console.log("PDS Sync Payload for updatePatientDetails:", payload);
  return payload;
}

export function PdsSyncDialog({
  open,
  onOpenChange,
  localRecord,
  pdsRecord = null,
  onApply,
  onSyncFromPds,
  isLoading = false,
  onDarkMode = false,
}: PdsSyncDialogProps) {
  const { toast } = useToast();
  const { appState } = useTitanApp();
  const currentUser = appState?.currentUser;
  const currentUserEmail =
    currentUser?.emailAddress || currentUser?.email || "";

  const [choices, setChoices] = useState<Record<string, Choice>>({});
  // Contacts & addresses are combinable (keep existing + apply new), so each
  // side is an independent include toggle rather than an either/or choice.
  const [keepLocalPhones, setKeepLocalPhones] = useState(true);
  const [applyPdsPhones, setApplyPdsPhones] = useState(true);
  const [keepLocalAddresses, setKeepLocalAddresses] = useState(true);
  const [applyPdsAddresses, setApplyPdsAddresses] = useState(true);
  const [selectedColumn, setSelectedColumn] = useState<Choice | null>(null);
  const [preferredPhone, setPreferredPhone] = useState<string | null>(null);
  const [preferredAddress, setPreferredAddress] = useState<string | null>(null);
  const [hideIdentical, setHideIdentical] = useState(true);
  const [step, setStep] = useState<"compare" | "review">("compare");
  const [isSyncing, setIsSyncing] = useState(false);

  const local: PdsDemographics = useMemo(
    () => toPdsDemographics(localRecord),
    [localRecord],
  );

  const pds: PdsRecord = useMemo(
    () => toPdsDemographics(pdsRecord),
    [pdsRecord],
  );

  const isSame = (key: keyof PdsDemographics) =>
    normField(local, key) === normField(pds, key);

  // Default single-value fields to PDS (authoritative); default contacts and
  // addresses to keep existing AND apply new (the union).
  useEffect(() => {
    if (!open) return;
    const initial: Record<string, Choice> = {};
    for (const { key, kind } of PDS_FIELDS) {
      if (kind === "text") initial[key] = isSame(key) ? "local" : "pds";
    }
    setChoices(initial);
    setKeepLocalPhones(true);
    setApplyPdsPhones(true);
    setKeepLocalAddresses(true);
    setApplyPdsAddresses(true);
    setSelectedColumn(null);
    setPreferredPhone(null);
    setPreferredAddress(null);
    setStep("compare");
    setIsSyncing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, local, pds]);

  const differingCount = PDS_FIELDS.filter(({ key }) => !isSame(key)).length;

  const setAll = (choice: Choice) => {
    const next: Record<string, Choice> = {};
    for (const { key, kind } of PDS_FIELDS) {
      if (kind === "text") next[key] = isSame(key) ? "local" : choice;
    }
    setChoices(next);
    // Keep both sides when identical so nothing is lost; otherwise follow choice.
    setKeepLocalPhones(isSame("phones") ? true : choice === "local");
    setApplyPdsPhones(isSame("phones") ? true : choice === "pds");
    setKeepLocalAddresses(isSame("addresses") ? true : choice === "local");
    setApplyPdsAddresses(isSame("addresses") ? true : choice === "pds");
    setSelectedColumn(choice);
  };

  const result = useMemo<PdsDemographics>(() => {
    const r = {} as PdsDemographics;
    for (const { key, kind } of PDS_FIELDS) {
      if (kind === "phones") {
        const phones = dedupePhones([
          ...(keepLocalPhones ? local.phones : []),
          ...(applyPdsPhones ? pds.phones : []),
        ]);
        const pref =
          phones.find((p) => phoneKey(p) === preferredPhone) ?? phones[0];
        r.phones = phones.map((p) => ({ ...p, preferred: p === pref }));
      } else if (kind === "addresses") {
        const addresses = dedupeAddresses([
          ...(keepLocalAddresses ? local.addresses : []),
          ...(applyPdsAddresses ? pds.addresses : []),
        ]);
        const pref =
          addresses.find((a) => addressKey(a) === preferredAddress) ??
          addresses[0];
        r.addresses = addresses.map((a) => ({ ...a, preferred: a === pref }));
      } else {
        (r as any)[key] =
          (choices[key] ?? "local") === "pds" ? pds[key] : local[key];
      }
    }
    return r;
  }, [
    choices,
    local,
    pds,
    keepLocalPhones,
    applyPdsPhones,
    keepLocalAddresses,
    applyPdsAddresses,
    preferredPhone,
    preferredAddress,
  ]);

  // Number of fields whose resulting value differs from the current local record.
  const changedCount = PDS_FIELDS.filter(
    ({ key }) => normField(result, key) !== normField(local, key),
  ).length;

  // True when the raw PDS API response was null, or came back with every
  // field null/0/empty — i.e. no real NHS record was found. Guards against
  // syncing blanks over the patient's existing local data.
  const pdsResponseIsEmpty = isEmptyPdsResponse(pdsRecord);

  const handleConfirm = async () => {
    if (pdsResponseIsEmpty) {
      toast({
        variant: "destructive",
        title: "No PDS record found",
        description:
          "The NHS Personal Demographics Service returned no data for this patient, so nothing can be synced.",
      });
      return;
    }
    setIsSyncing(true);
    try {
      console.log("Selected result from modal:", result);
      console.log("Local record:", localRecord);
      if (onSyncFromPds) {
        await onSyncFromPds(result, localRecord);
      } else {
        const payload = buildUpdatePayload(
          localRecord,
          pdsRecord,
          result,
          currentUserEmail,
        );
        console.log("Updating local record with payload:", payload);
        const response = await updatePatientDetails(payload);

        if (
          response !== "true" &&
          typeof response === "string" &&
          response.length > 0
        ) {
          toast({
            variant: "destructive",
            title: "Failed to update patient details",
            description: response,
          });
          setIsSyncing(false);
          return;
        }

        onApply?.(result);
        toast({
          title: "Local record updated from PDS",
          description:
            changedCount > 0
              ? `${changedCount} field${changedCount > 1 ? "s" : ""} updated from the NHS Personal Demographics Service.`
              : "No changes were applied — local record kept.",
        });
        onOpenChange(false);

        const patientId =
          localRecord?.patientID || localRecord?.patientId || localRecord?._id;
        if (patientId) {
          getPatientByPatientId(patientId);
        }
        return;
      }

      onApply?.(result);
      toast({
        title: "Local record updated from PDS",
        description:
          changedCount > 0
            ? `${changedCount} field${changedCount > 1 ? "s" : ""} updated from the NHS Personal Demographics Service.`
            : "No changes were applied — local record kept.",
      });
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error updating patient details from PDS:", error);
      toast({
        variant: "destructive",
        title: "PDS Sync Error",
        description: error?.message || "Failed to update patient record.",
      });
    } finally {
      setIsSyncing(false);
    }
  };

  // const display = (v: string) => (v && v.trim() ? v : "—");

  const display = (v: unknown) => {
    if (v === null || v === undefined) return "—";
    if (typeof v === "string") {
      return v.trim() || "—";
    }
    return String(v);
  };

  const renderValue = (
    rec: PdsDemographics,
    key: keyof PdsDemographics,
    kind: PdsFieldKind,
  ) => {
    if (kind === "phones") {
      if (!rec.phones.length) return <span className={cn("text-sm", onDarkMode && "text-slate-400")}>—</span>;
      return (
        <div className="space-y-1">
          {rec.phones.map((p, i) => (
            <div key={i} className="flex items-center gap-2">
              <Badge variant="outline" className={cn("text-[10px] shrink-0", onDarkMode && "border-slate-700 text-slate-300 bg-slate-800/60")}>
                {p.label}
              </Badge>
              <span className={cn("text-sm break-words", onDarkMode && "text-slate-200")}>{p.value}</span>
            </div>
          ))}
        </div>
      );
    }
    if (kind === "addresses") {
      if (!rec.addresses.length) return <span className={cn("text-sm", onDarkMode && "text-slate-400")}>—</span>;
      return (
        <div className="space-y-1.5">
          {rec.addresses.map((a, i) => (
            <div key={i} className="flex items-start gap-2">
              <Badge variant="outline" className={cn("text-[10px] shrink-0 mt-0.5", onDarkMode && "border-slate-700 text-slate-300 bg-slate-800/60")}>
                {a.label}
              </Badge>
              <span className={cn("text-sm break-words", onDarkMode && "text-slate-200")}>{a.text}</span>
            </div>
          ))}
        </div>
      );
    }
    return (
      <span className={cn("text-sm break-words", onDarkMode && "text-slate-200")}>{display(rec[key] as string)}</span>
    );
  };

  const includeCell = (
    checked: boolean,
    onChange: (v: boolean) => void,
    rec: PdsDemographics,
    key: keyof PdsDemographics,
    kind: PdsFieldKind,
    caption: string,
    highlightClass: string,
    darkHighlightClass: string,
    disabled: boolean,
  ) => (
    <label
      className={cn(
        "px-3 py-2 flex items-start gap-2",
        disabled ? "cursor-default" : "cursor-pointer",
        checked && (onDarkMode ? darkHighlightClass : highlightClass),
        onDarkMode && !checked && "hover:bg-slate-800/30 text-slate-300",
      )}
    >
      <Checkbox
        checked={checked}
        onCheckedChange={(v) => {
          onChange(!!v);
          setSelectedColumn(null);
        }}
        disabled={disabled}
        className="shrink-0 mt-0.5"
      />
      <div className="space-y-1 min-w-0">
        <span className={cn("block text-[11px] font-medium text-muted-foreground", onDarkMode && "text-slate-400")}>
          {caption}
        </span>
        {renderValue(rec, key, kind)}
      </div>
    </label>
  );

  const preferredList = (
    entries: (PdsContactPoint | PdsAddressEntry)[],
    onChange: (key: string) => void,
  ) => {
    const selected = entries.find((e) => e.preferred);
    return (
      <RadioGroup
        value={selected ? entryKey(selected) : ""}
        onValueChange={onChange}
        className="space-y-1"
      >
        {entries.map((e, i) => {
          const text = "value" in e ? e.value : e.text;
          return (
            <label
              key={i}
              className={cn(
                "flex items-start gap-2 cursor-pointer rounded-md px-1 py-0.5 hover:bg-muted/50",
                onDarkMode && "hover:bg-slate-800/60 text-slate-200",
              )}
            >
              <RadioGroupItem value={entryKey(e)} className="shrink-0 mt-0.5" />
              <Badge variant="outline" className={cn("text-[10px] shrink-0 mt-0.5", onDarkMode && "border-slate-700 text-slate-300 bg-slate-800/60")}>
                {e.label}
              </Badge>
              <span className="text-sm break-words flex-1">{text}</span>
              {e.preferred && (
                <Badge
                  className="gap-0.5 text-[10px] text-white shrink-0"
                  style={{ backgroundColor: NHS_BLUE }}
                >
                  <Star className="w-2.5 h-2.5 fill-current" />
                  Preferred
                </Badge>
              )}
            </label>
          );
        })}
      </RadioGroup>
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !isSyncing && onOpenChange(v)}>
        <DialogContent className={cn("max-w-4xl max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col", isSyncing && "pointer-events-none opacity-80", onDarkMode && "dark bg-slate-900 text-slate-100 border-slate-800")}>
          <DialogHeader className={cn("px-6 py-4 border-b shrink-0 space-y-2", onDarkMode && "border-slate-800 bg-slate-900")}>
            <DialogTitle className={cn("flex items-center gap-2", onDarkMode && "text-slate-100")}>
              <span
                className="inline-flex items-center rounded-sm px-1.5 py-0.5 text-[11px] font-bold italic leading-none tracking-tight text-white"
                style={{ backgroundColor: NHS_BLUE }}
              >
                NHS
              </span>
              PDS Sync — Personal Demographics Service
            </DialogTitle>
            <DialogDescription className={cn(onDarkMode && "text-slate-400")}>
              {step === "compare"
                ? "Step 1 of 2 · Compare — choose which demographic values to bring across. PDS is the authoritative source, so differing fields default to the NHS value."
                : "Step 2 of 2 · Review — confirm the resulting local record and set the preferred contact number and address before syncing."}
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <PdsSkeleton onDarkMode={onDarkMode} />
          ) : (
            <>
              {step === "compare" && (
                <div className="flex-1 flex flex-col min-h-0">
                  {/* PDS record banner */}
                  <div className="px-6 pt-4">
                    <div
                      className={cn(
                        "flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3",
                        onDarkMode && "border-blue-900/40 bg-blue-950/20 text-slate-200",
                      )}
                      style={
                        !onDarkMode
                          ? {
                              borderColor: `${NHS_BLUE}33`,
                              backgroundColor: `${NHS_BLUE}0a`,
                            }
                          : {}
                      }
                    >
                      <div className="flex items-center gap-2 text-sm">
                        <span className={cn("font-semibold", onDarkMode && "text-slate-100")}>
                          {localRecord?.patientName || "Unknown"}
                        </span>
                        <span className={cn("text-muted-foreground", onDarkMode && "text-slate-400")}>
                          NHS {localRecord?.nhsNumber || "—"}
                        </span>
                        {localRecord?.nhsNumber && (
                          <Badge
                            className="gap-1 text-white hover:opacity-90"
                            style={{ backgroundColor: NHS_BLUE }}
                          >
                            <ShieldCheck className="w-3 h-3" />
                            Verified
                          </Badge>
                        )}
                      </div>
                      <span className={cn("text-xs text-muted-foreground", onDarkMode && "text-slate-400")}>
                        {differingCount === 0
                          ? "No differences detected"
                          : `${differingCount} field${differingCount > 1 ? "s" : ""} differ from local`}
                      </span>
                    </div>
                  </div>

                  {/* Controls */}
                  <div className="flex items-center gap-2 px-6 py-3">
                    <Switch
                      id="pds-hide-identical"
                      checked={hideIdentical}
                      onCheckedChange={setHideIdentical}
                    />
                    <Label
                      htmlFor="pds-hide-identical"
                      className={cn("text-sm text-muted-foreground cursor-pointer", onDarkMode && "text-slate-400")}
                    >
                      Hide identical fields
                    </Label>
                  </div>

                  <ScrollArea className="flex-1 min-h-0">
                    <div className="px-6 pb-4">
                      <div className={cn("rounded-lg border", onDarkMode ? "border-slate-800 bg-slate-900/50" : "")}>
                        <div className={cn("grid grid-cols-3 text-xs border-b", onDarkMode ? "border-slate-800 text-slate-400 bg-slate-800/40" : "text-muted-foreground")}>
                          <div className="px-3 py-2">Field</div>
                          <button
                            type="button"
                            onClick={() => setAll("local")}
                            className={cn("px-3 py-2 flex items-center gap-2 hover:opacity-70 transition-opacity", onDarkMode && "text-slate-300")}
                            title="Keep all from the local record"
                          >
                            <span className={cn("w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0", onDarkMode ? "border-slate-600" : "border-gray-400")}>
                              {selectedColumn === "local" && (
                                <span className={cn("w-2 h-2 rounded-full", onDarkMode ? "bg-slate-300" : "bg-gray-600")} />
                              )}
                            </span>
                            <span>Local record</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setAll("pds")}
                            className={cn("px-3 py-2 flex items-center gap-2 hover:opacity-70 transition-opacity", onDarkMode && "text-slate-300")}
                            title="Use all from the NHS PDS record"
                          >
                            <span className={cn("w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0", onDarkMode ? "border-slate-600" : "border-gray-400")}>
                              {selectedColumn === "pds" && (
                                <span className={cn("w-2 h-2 rounded-full", onDarkMode ? "bg-slate-300" : "bg-gray-600")} />
                              )}
                            </span>
                            <span
                              className="inline-flex items-center rounded-sm px-1 py-0.5 text-[9px] font-bold italic leading-none text-white"
                              style={{ backgroundColor: NHS_BLUE }}
                            >
                              NHS
                            </span>
                            <span>PDS (Spine)</span>
                          </button>
                        </div>

                        {PDS_FIELDS.map(({ key, label, kind }) => {
                          const same = isSame(key);
                          if (hideIdentical && same) return null;
                          const isMulti = kind !== "text";
                          return (
                            <div
                              key={key}
                              className={cn(
                                "grid grid-cols-3 border-b last:border-b-0",
                                isMulti ? "items-start" : "items-center",
                                onDarkMode ? "border-slate-800" : "",
                                same && (onDarkMode ? "bg-slate-800/30" : "bg-muted/50"),
                              )}
                            >
                              <div className={cn("px-3 py-2 text-sm flex items-center gap-2", onDarkMode && "text-slate-200")}>
                                {label}
                                {same && (
                                  <Badge
                                    variant="outline"
                                    className={cn("text-[10px] shrink-0", onDarkMode && "border-slate-700 text-slate-400 bg-slate-800/50")}
                                  >
                                    Same
                                  </Badge>
                                )}
                              </div>
                              {kind === "text" ? (
                                <RadioGroup
                                  value={choices[key] ?? "local"}
                                  onValueChange={(v) => {
                                    setChoices((c) => ({
                                      ...c,
                                      [key]: v as Choice,
                                    }));
                                    setSelectedColumn(null);
                                  }}
                                  className="contents"
                                >
                                  <label
                                    className={cn(
                                      "px-3 py-2 flex items-start gap-2 cursor-pointer",
                                      !same &&
                                        choices[key] === "local" &&
                                        (onDarkMode ? "bg-amber-950/40 text-amber-200" : "bg-amber-50 dark:bg-amber-900/20"),
                                      onDarkMode && choices[key] !== "local" && "hover:bg-slate-800/30 text-slate-300",
                                    )}
                                  >
                                    <RadioGroupItem
                                      value="local"
                                      disabled={same}
                                      className="shrink-0 mt-0.5"
                                    />
                                    {renderValue(local, key, kind)}
                                  </label>
                                  <label
                                    className={cn(
                                      "px-3 py-2 flex items-start gap-2 cursor-pointer",
                                      !same &&
                                        choices[key] === "pds" &&
                                        (onDarkMode ? "bg-blue-950/40 text-blue-200" : "bg-[#005EB8]/10"),
                                      onDarkMode && choices[key] !== "pds" && "hover:bg-slate-800/30 text-slate-300",
                                    )}
                                  >
                                    <RadioGroupItem
                                      value="pds"
                                      disabled={same}
                                      className="shrink-0 mt-0.5"
                                    />
                                    {renderValue(pds, key, kind)}
                                  </label>
                                </RadioGroup>
                              ) : kind === "phones" ? (
                                <>
                                  {includeCell(
                                    keepLocalPhones,
                                    setKeepLocalPhones,
                                    local,
                                    key,
                                    kind,
                                    "Keep existing",
                                    "bg-amber-50 dark:bg-amber-900/20",
                                    "bg-amber-950/40 text-amber-200",
                                    same,
                                  )}
                                  {includeCell(
                                    applyPdsPhones,
                                    setApplyPdsPhones,
                                    pds,
                                    key,
                                    kind,
                                    "Apply from PDS",
                                    "bg-[#005EB8]/10",
                                    "bg-blue-950/40 text-blue-200",
                                    same,
                                  )}
                                </>
                              ) : (
                                <>
                                  {includeCell(
                                    keepLocalAddresses,
                                    setKeepLocalAddresses,
                                    local,
                                    key,
                                    kind,
                                    "Keep existing",
                                    "bg-amber-50 dark:bg-amber-900/20",
                                    "bg-amber-950/40 text-amber-200",
                                    same,
                                  )}
                                  {includeCell(
                                    applyPdsAddresses,
                                    setApplyPdsAddresses,
                                    pds,
                                    key,
                                    kind,
                                    "Apply from PDS",
                                    "bg-[#005EB8]/10",
                                    "bg-blue-950/40 text-blue-200",
                                    same,
                                  )}
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </ScrollArea>
                </div>
              )}

              {step === "review" && (
                <ScrollArea className="flex-1 min-h-0">
                  <div className="px-6 py-4">
                    <div className={cn("text-sm font-medium mb-2 flex items-center gap-2", onDarkMode ? "text-slate-200" : "")}>
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      Resulting local record
                    </div>
                    <div className={cn("rounded-lg border p-4 space-y-3", onDarkMode ? "border-slate-800 bg-slate-900/50" : "")}>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                        {PDS_FIELDS.filter((f) => f.kind === "text").map(
                          ({ key, label, kind }) => (
                            <div key={key} className="flex gap-2 min-w-0">
                              <span className={cn("text-muted-foreground shrink-0", onDarkMode && "text-slate-400")}>
                                {label}:
                              </span>
                              <span className={cn("break-words font-medium", onDarkMode && "text-slate-200")}>
                                {renderValue(result, key, kind)}
                              </span>
                            </div>
                          ),
                        )}
                      </div>

                      {result.phones.length > 0 && (
                        <div>
                          <div className={cn("text-xs text-muted-foreground mb-1.5", onDarkMode && "text-slate-400")}>
                            Contact numbers — select preferred
                          </div>
                          {preferredList(result.phones, setPreferredPhone)}
                        </div>
                      )}

                      {result.addresses.length > 0 && (
                        <div>
                          <div className={cn("text-xs text-muted-foreground mb-1.5", onDarkMode && "text-slate-400")}>
                            Addresses — select preferred
                          </div>
                          {preferredList(result.addresses, setPreferredAddress)}
                        </div>
                      )}
                    </div>
                  </div>
                </ScrollArea>
              )}
            </>
          )}

          {!isLoading && (
            <DialogFooter className={cn("px-6 py-4 border-t shrink-0", onDarkMode && "border-slate-800 bg-slate-900")}>
              {step === "compare" ? (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => onOpenChange(false)}
                    disabled={isSyncing}
                    className={cn(onDarkMode && "text-slate-300 hover:bg-slate-800 hover:text-slate-100")}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="text-white hover:opacity-90"
                    style={{ backgroundColor: NHS_BLUE }}
                    onClick={() => setStep("review")}
                    disabled={isSyncing}
                  >
                    Review changes
                    {changedCount > 0 ? ` (${changedCount})` : ""}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => setStep("compare")}
                    disabled={isSyncing}
                    className={cn(onDarkMode && "text-slate-300 hover:bg-slate-800 hover:text-slate-100")}
                  >
                    <ArrowLeft className="mr-1.5 h-4 w-4" />
                    Back
                  </Button>
                  <Button
                    className="text-white hover:opacity-90 min-w-[140px]"
                    style={{ backgroundColor: NHS_BLUE }}
                    onClick={handleConfirm}
                    disabled={isSyncing || pdsResponseIsEmpty}
                    title={
                      pdsResponseIsEmpty
                        ? "No PDS record found — nothing to sync"
                        : undefined
                    }
                  >
                    {isSyncing ? (
                      <>
                        <Loader2 className="animate-spin mr-2 h-4 w-4" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Sync from PDS
                        {changedCount > 0 ? ` (${changedCount})` : ""}
                      </>
                    )}
                  </Button>
                </>
              )}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
